/**
 * Axios factory for kiosk service clients.
 *
 * All upstream services share the same response-error normalisation:
 * we turn any 4xx/5xx into a plain `Error` whose message is the most
 * informative string we can find. Screens just `try/catch` on `Error.message`
 * without having to dig into axios internals.
 */

import axios, { type AxiosInstance } from 'axios';
import type { z } from 'zod';

import { env } from '../env';
import { getErrorMessage } from '../get-error-message';

interface CreateClientOptions {
  baseURL: string;
  timeoutMs?: number;
  /** Extra static headers to merge on top of the defaults. */
  extraHeaders?: Record<string, string>;
}

function normaliseError(error: unknown): Error {
  const e = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  const message =
    e.response?.data?.message ??
    e.response?.data?.error ??
    getErrorMessage(error);
  return new Error(message);
}

export function createClient(options: CreateClientOptions): AxiosInstance {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.extraHeaders ?? {}),
  };

  const client = axios.create({
    baseURL: options.baseURL,
    timeout: options.timeoutMs ?? 15_000,
    headers,
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(normaliseError(error)),
  );

  return client;
}

/** Standard success envelope used by NestJS services in this monorepo. */
export type ApiEnvelope<T> = { success: boolean; data: T };

/**
 * Thrown by `unwrapAndParse` when a response doesn't match the expected
 * schema. Carries Zod issues + the raw payload for logging/telemetry.
 */
export class ApiResponseValidationError extends Error {
  readonly code = 'RESPONSE_VALIDATION_ERROR';

  constructor(
    public readonly issues: z.ZodIssue[],
    public readonly raw: unknown,
  ) {
    super('Received malformed data from the server.');
    this.name = 'ApiResponseValidationError';
  }
}

/**
 * Strip a NestJS `{ success, data }` envelope when present.
 * Direct payloads (e.g. identity login, wristband public lookup) pass through.
 */
export function unwrapEnvelope(body: unknown): unknown {
  if (body !== null && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as ApiEnvelope<unknown>).data;
  }
  return body;
}

/**
 * Unwraps an optional axios/fetch `{ data }` wrapper, then any service envelope,
 * and validates the payload against `schema`.
 */
export function unwrapAndParse<T extends z.ZodTypeAny>(
  body: unknown,
  schema: T,
): z.infer<T> {
  const withDataWrapper =
    body !== null && typeof body === 'object' && 'data' in body
      ? (body as { data?: unknown }).data ?? body
      : body;
  const raw = unwrapEnvelope(withDataWrapper);
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ApiResponseValidationError(parsed.error.issues, raw);
  }
  return parsed.data;
}

// ─── Pre-built clients ───────────────────────────────────────────────────────

export const identityApi = createClient({
  baseURL: `${env.identityBaseUrl}/api/v1`,
  timeoutMs: 10_000,
});

export const kioskApi = createClient({
  baseURL: `${env.kioskBaseUrl}/api/v1`,
});

/**
 * Attach the KIOSK staff JWT, written to localStorage on login and cleared on logout.
 *
 * Named rather than inlined per client because forgetting it is silent: the request
 * still goes out, the service answers 401, and the screen renders as if the data were
 * simply empty. `capacityApi` was missing it, so
 * `GET /capacity/attractions/slots/batch` 401'd on the walk-up flow's Wahana step and
 * no attraction had any time slot to choose.
 */
function attachKioskStaffToken(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('kiosk_staff_token');
    if (token) {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  });
}

attachKioskStaffToken(kioskApi);

export const fnbApi = createClient({
  baseURL: `${env.fnbBaseUrl}/api/v1`,
});

export const ticketingApi = createClient({
  baseURL: `${env.ticketingBaseUrl}/api/v1`,
});

export const wristbandApi = createClient({
  baseURL: `${env.wristbandBaseUrl}/api/v1`,
  timeoutMs: 10_000,
});

export const capacityApi = createClient({
  baseURL: `${env.capacityBaseUrl}/api/v1`,
  timeoutMs: 10_000,
});

// The slot endpoints are JWT-guarded, not public.
attachKioskStaffToken(capacityApi);

export const analyticsApi = createClient({
  baseURL: `${env.analyticsBaseUrl}/api/v1`,
  timeoutMs: 10_000,
});

// KioskPhotosController requires a KIOSK-role JWT — same token as kioskApi.
attachKioskStaffToken(analyticsApi);
